import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { runServerFn } from "@/lib/server-fn";
import {
  allocateOrder,
  confirmPickOrder,
  shipOrder,
  addOrderLine,
} from "@/lib/orders.functions";
import { upsertOrderLine, removeOrderLine } from "@/lib/orders-edit.functions";
import { OrderWaveMemberships } from "@/components/orders/order-wave-memberships";
import { updateOrderRouting } from "@/lib/orders-edit.functions";
import { getOrderCostBreakdown } from "@/lib/order-cost.functions";
import { createRmaFromOrder } from "@/lib/return-labels.functions";
import {
  getOrder,
  getOrderLines,
  getOrderAllocations,
  listCustomerSkus,
  listFacilityLocations,
  listTenantCarriers,
  listOrderShipments,
} from "@/lib/operations.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Loader2, Boxes, PackageCheck, Truck, FileText, Undo2, ArrowLeft, Printer, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { ShipmentConsumables } from "@/components/shipment-consumables";
import { CustodyTimeline } from "@/components/inventory/custody-timeline";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OrderStatusActions, OrderTimeline } from "@/components/orders/order-status-actions";
import { OrderDetailsEdit } from "@/components/orders/order-details-edit";
import { SkuCombobox } from "@/components/orders/sku-combobox";
import { OrderRmasPanel } from "@/components/orders/order-rmas-panel";
import { OrderHoldsPanel } from "@/components/orders/order-holds-panel";
import { canEdit, type OrderStatus } from "@/lib/orders/state-machine";
import { PageHeader } from "@/components/ui/page-header";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { OrderSummaryHeader } from "@/components/orders/order-summary-header";
import { getOrderOperationalSummary } from "@/lib/orders/summary.functions";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Check, X } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/orders/$orderId")({ component: OrderDetailPage });

function OrderDetailPage() {
  const { orderId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const fetchOrder = useServerFn(getOrder);
  const fetchOrderLines = useServerFn(getOrderLines);
  const fetchAllocations = useServerFn(getOrderAllocations);
  const fetchSkus = useServerFn(listCustomerSkus);
  const fetchLocations = useServerFn(listFacilityLocations);
  const fetchCarriers = useServerFn(listTenantCarriers);
  const fetchOrderShipments = useServerFn(listOrderShipments);
  const fetchOrderSummary = useServerFn(getOrderOperationalSummary);

  const { data: order } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => fetchOrder({ data: { orderId } }),
  });

  const { data: summary } = useQuery({
    queryKey: ["order-summary", orderId],
    queryFn: () => fetchOrderSummary({ data: { orderId } }),
  });

  const { data: lines, refetch: refetchLines } = useQuery({
    queryKey: ["order-lines", orderId],
    queryFn: () => fetchOrderLines({ data: { orderId } }),
  });

  const { data: allocations } = useQuery({
    queryKey: ["allocations", orderId],
    queryFn: () => fetchAllocations({ data: { orderId } }),
  });

  const { data: skus } = useQuery({
    queryKey: ["skus-for-order", order?.customer_id],
    enabled: !!order?.customer_id,
    queryFn: () => fetchSkus({ data: { customerId: order!.customer_id } }),
  });

  const { data: locations } = useQuery({
    queryKey: ["locations-for-order", order?.facility_id],
    enabled: !!order?.facility_id,
    queryFn: () => fetchLocations({ data: { facilityId: order!.facility_id } }),
  });

  const { data: orderShipments = [] } = useQuery({
    queryKey: ["order-shipments", orderId],
    queryFn: () => fetchOrderShipments({ data: { orderId } }),
  });

  const reprintLabel = (labelUrl: string) => {
    const w = window.open(labelUrl, "_blank", "noopener,noreferrer");
    if (!w) {
      toast.warning("Pop-up blocked — allow pop-ups to reprint the label");
      return;
    }
    try {
      w.addEventListener("load", () => {
        try { w.focus(); w.print(); } catch { /* user prints manually */ }
      });
    } catch { /* cross-origin storage URL */ }
    toast.success("Label opened — sending to printer");
  };

  const { data: carrierData } = useQuery({
    queryKey: ["order-carriers", order?.tenant_id],
    enabled: !!order?.tenant_id,
    queryFn: () => fetchCarriers({ data: { tenantId: order!.tenant_id } }),
  });
  const carriers = carrierData?.carriers ?? [];
  const carrierServices = carrierData?.services ?? [];

  const skuLabel = (id: string) => { const s = skus?.find((x) => x.id === id); return s ? `${s.sku_code} — ${s.name}` : "—"; };
  const locLabel = (id: string) => locations?.find((l) => l.id === id)?.code ?? "—";

  const allocateFn = useServerFn(allocateOrder);
  const confirmPickFn = useServerFn(confirmPickOrder);
  const shipFn = useServerFn(shipOrder);
  const addLineFn = useServerFn(addOrderLine);
  const upsertLineFn = useServerFn(upsertOrderLine);
  const removeLineFn = useServerFn(removeOrderLine);
  const createRmaFn = useServerFn(createRmaFromOrder);

  const [addOpen, setAddOpen] = useState(false);
  const [skuId, setSkuId] = useState(""); const [qty, setQty] = useState(""); const [adding, setAdding] = useState(false);
  const addLine = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseInt(qty, 10);
    if (!skuId || !n || n <= 0) return toast.error("Pick a SKU and quantity");
    setAdding(true);
    try {
      await runServerFn(
        () => addLineFn({ data: { orderId, skuId, qty: n } }),
        "Add line",
      );
      toast.success("Line added");
      setAddOpen(false); setSkuId(""); setQty(""); refetchLines();
    } catch {
      // toast already surfaced by runServerFn
    } finally {
      setAdding(false);
    }
  };

  const [busy, setBusy] = useState<string | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["order", orderId] });
    qc.invalidateQueries({ queryKey: ["order-lines", orderId] });
    qc.invalidateQueries({ queryKey: ["allocations", orderId] });
    qc.invalidateQueries({ queryKey: ["balances"] });
  };

  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState("");
  const [lineBusy, setLineBusy] = useState<string | null>(null);

  const status = order?.status as OrderStatus | undefined;
  const canQtyChange = status ? (canEdit(status, "lines_qty_up") || canEdit(status, "lines_qty_down")) : false;
  const canLineRemove = status ? canEdit(status, "lines_remove") : false;

  const saveLineQty = async (line: { id: string; sku_id: string; qty_ordered: number }) => {
    const n = parseInt(editQty, 10);
    if (!n || n <= 0) return toast.error("Quantity must be a positive number");
    if (n === line.qty_ordered) { setEditingLineId(null); return; }
    setLineBusy(line.id);
    try {
      const res = (await runServerFn(
        () => upsertLineFn({ data: {
          orderId,
          expected_version: (order as any)?.version ?? 0,
          lineId: line.id, sku_id: line.sku_id, qty_ordered: n,
        } }),
        "Update line",
      )) as { ok: boolean; reason?: string };
      if (res && res.ok === false && res.reason === "version_conflict") {
        toast.error("Order changed since you opened it — reload to retry");
        refresh(); setEditingLineId(null);
        return;
      }
      toast.success("Line updated");
      setEditingLineId(null);
      refresh();
    } catch { /* toasted */ }
    finally { setLineBusy(null); }
  };

  const removeLine = async (lineId: string) => {
    setLineBusy(lineId);
    try {
      const res = (await runServerFn(
        () => removeLineFn({ data: {
          orderId,
          expected_version: (order as any)?.version ?? 0,
          lineId,
        } }),
        "Remove line",
      )) as { ok: boolean; reason?: string };
      if (res && res.ok === false && res.reason === "version_conflict") {
        toast.error("Order changed since you opened it — reload to retry");
        refresh();
        return;
      }
      toast.success("Line removed");
      refresh();
    } catch { /* toasted */ }
    finally { setLineBusy(null); }
  };

  const allocate = async () => {
    setBusy("allocate");
    try {
      await runServerFn(() => allocateFn({ data: { orderId } }), "Allocate");
      toast.success("Allocated"); refresh();
    } catch { /* toast handled */ }
    finally { setBusy(null); }
  };
  const confirmPick = async () => {
    setBusy("pick");
    try {
      await runServerFn(() => confirmPickFn({ data: { orderId } }), "Confirm pick");
      toast.success("Picked"); refresh();
    } catch { /* toast handled */ }
    finally { setBusy(null); }
  };

  const [shipOpen, setShipOpen] = useState(false);
  const [shipCarrier, setShipCarrier] = useState(""); const [shipService, setShipService] = useState(""); const [shipTracking, setShipTracking] = useState("");
  const ship = async (e: React.FormEvent) => {
    e.preventDefault();
    const carrier = shipCarrier || order?.carrier || "";
    const serviceLevel = shipService || order?.service_level || "";
    if (!carrier || !serviceLevel || !shipTracking) {
      return toast.error("Carrier, service, and tracking are required");
    }
    setBusy("ship");
    try {
      await runServerFn(
        () => shipFn({ data: { orderId, carrier, serviceLevel, tracking: shipTracking } }),
        "Ship",
      );
      toast.success("Shipped"); setShipOpen(false); refresh();
    } catch { /* toast handled */ }
    finally { setBusy(null); }
  };

  if (!order) return <div className="p-4 sm:p-6 text-muted-foreground">Loading…</div>;
  const shipTo = order.ship_to as { name?: string; address?: string } | null;
  const canEditBasic = order.status === "draft" || order.status === "open";
  const canAllocate = canEditBasic && (lines?.length ?? 0) > 0;
  const canPick = order.status === "allocated" && (allocations?.some(a => a.status === "allocated") ?? false);
  const canShip = order.status === "picked";

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 -ml-2 shrink-0"
              onClick={() => navigate({ to: "/app/orders" })}
              aria-label="Back to orders"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="font-mono text-lg sm:text-xl tracking-tight truncate">{order.order_number}</span>
          </span>
        }
        description={shipTo?.name ? `Ship to ${shipTo.name}` : undefined}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={allocate} disabled={!canAllocate || busy === "allocate"}>
              {busy === "allocate" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Boxes className="h-4 w-4 mr-2" />}Allocate
            </Button>
            <Button size="sm" variant="outline" onClick={confirmPick} disabled={!canPick || busy === "pick"}>
              {busy === "pick" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackageCheck className="h-4 w-4 mr-2" />}Confirm pick
            </Button>
            <Button size="sm" onClick={() => setShipOpen(true)} disabled={!canShip || busy === "ship"}>
              <Truck className="h-4 w-4 mr-2" />Ship
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/app/orders/$orderId/packing-slip" params={{ orderId }} target="_blank">
                <FileText className="h-4 w-4 mr-2" />Packing slip
              </Link>
            </Button>
            <CreateRmaButton
              orderId={orderId}
              orderNumber={order.order_number}
              lines={(lines ?? []).map((l) => ({ id: l.id, sku_id: l.sku_id, qty_shipped: l.qty_shipped ?? 0, qty_ordered: l.qty_ordered }))}
              skuLabel={skuLabel}
              createRmaFn={createRmaFn}
              onCreated={(rmaId: string) => navigate({ to: "/app/returns", search: { rmaId } })}
            />
          </div>
        }
      />
      {summary && (
        <div className="px-4 sm:px-6 -mt-2 pb-1">
          <OrderSummaryHeader summary={summary} variant="internal" />
        </div>
      )}

      <div className="p-4 sm:p-6 grid gap-4 lg:gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Order Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <OrderDetailsEdit
            orderId={orderId}
            tenantId={order.tenant_id}
            status={order.status as OrderStatus}
            version={(order as any).version ?? 0}
            initial={{
              ship_to: (order as any).ship_to ?? null,
              billing_to: (order as any).billing_to ?? null,
              customer_contact: (order as any).customer_contact ?? null,
              carrier: order.carrier ?? null,
              service_level: order.service_level ?? null,
              customer_po: (order as any).customer_po ?? null,
              requested_ship_date: (order as any).requested_ship_date ?? null,
              priority: (order as any).priority ?? null,
              tags: (order as any).tags ?? null,
              notes: (order as any).notes ?? null,
              internal_notes: (order as any).internal_notes ?? null,
              custom_fields: (order as any).custom_fields ?? null,
            }}
              />
            </CardContent>
          </Card>

          <OrderRoutingCard
            orderId={orderId}
            status={order.status as OrderStatus}
            initialSizeTier={(order as any).size_tier ?? null}
            initialStrategy={(order as any).routing_strategy ?? null}
            onSaved={() => qc.invalidateQueries({ queryKey: ["order", orderId] })}
          />

          <OrderCostCard orderId={orderId} />

          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Lines</CardTitle>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild><Button size="sm" variant="outline" disabled={!canEditBasic}><Plus className="h-4 w-4 mr-2" />Add line</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add order line</DialogTitle></DialogHeader>
                  <form onSubmit={addLine} className="space-y-4">
                    <div className="space-y-2"><Label>SKU</Label>
                      <SkuCombobox
                        tenantId={order.tenant_id}
                        facilityId={order.facility_id}
                        value={skuId}
                        valueLabel={skuId ? skuLabel(skuId) : undefined}
                        onSelect={(s) => setSkuId(s.id)}
                      />
                    </div>
                    <div className="space-y-2"><Label>Quantity</Label><Input type="number" min={1} required value={qty} onChange={(e) => setQty(e.target.value)} /></div>
                    <DialogFooter><Button type="submit" disabled={adding}>{adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead className="text-right">Ordered</TableHead><TableHead className="text-right">Allocated</TableHead><TableHead className="text-right">Picked</TableHead><TableHead className="text-right">Shipped</TableHead><TableHead className="w-[140px] text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {lines && lines.length > 0 ? lines.map((l) => {
                    const isEditing = editingLineId === l.id;
                    const rowBusy = lineBusy === l.id;
                    const cannotRemove = !canLineRemove || (l.qty_picked ?? 0) > 0 || (l.qty_shipped ?? 0) > 0;
                    return (
                      <TableRow key={l.id}>
                        <TableCell>{skuLabel(l.sku_id)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {isEditing ? (
                            <Input
                              type="number" min={1}
                              value={editQty}
                              onChange={(e) => setEditQty(e.target.value)}
                              className="h-8 w-20 ml-auto text-right"
                              autoFocus
                            />
                          ) : l.qty_ordered}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{l.qty_allocated}</TableCell>
                        <TableCell className="text-right tabular-nums">{l.qty_picked}</TableCell>
                        <TableCell className="text-right tabular-nums">{l.qty_shipped}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {isEditing ? (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  disabled={rowBusy}
                                  onClick={() => saveLineQty(l)}
                                  aria-label="Save quantity">
                                  {rowBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  disabled={rowBusy}
                                  onClick={() => setEditingLineId(null)}
                                  aria-label="Cancel">
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" className="h-7 px-2"
                                  disabled={!canQtyChange || rowBusy}
                                  onClick={() => { setEditingLineId(l.id); setEditQty(String(l.qty_ordered)); }}>
                                  Edit
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive"
                                      disabled={cannotRemove || rowBusy}
                                      aria-label="Remove line">
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Remove line?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will remove <span className="font-medium">{skuLabel(l.sku_id)}</span> from the order. If the order is allocated, it will be reallocated.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => removeLine(l.id)}>Remove</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }) : (<TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No lines yet.</TableCell></TableRow>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {allocations && allocations.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Allocations</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Bin</TableHead><TableHead>Lot</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {allocations.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{skuLabel(a.sku_id)}</TableCell>
                        <TableCell>{locLabel(a.location_id)}</TableCell>
                        <TableCell className="text-muted-foreground">{a.lot ?? "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{a.qty}</TableCell>
                        <TableCell><Badge variant="outline">{a.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {order.status === "shipped" && (
            <ShipmentConsumables orderId={orderId} tenantId={order.tenant_id} />
          )}

          {orderShipments.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck className="h-4 w-4" /> Shipments &amp; labels
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {orderShipments.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 border rounded-md p-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium">
                        {(s.carrier ?? "—")}{s.service_level ? ` · ${s.service_level}` : ""}
                        <Badge variant="outline" className="ml-2 capitalize">{s.status}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {s.tracking_number ?? "No tracking yet"}
                        {typeof s.rate_cents === "number" && s.rate_cents > 0
                          ? ` · $${(s.rate_cents / 100).toFixed(2)}`
                          : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {s.label_url ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => reprintLabel(s.label_url!)}
                          >
                            <Printer className="h-4 w-4 mr-2" />Reprint label
                          </Button>
                          <a href={s.label_url} target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="h-4 w-4 mr-2" />View
                            </Button>
                          </a>
                        </>
                      ) : (
                        <Link to="/app/shipments/$shipmentId" params={{ shipmentId: s.id }}>
                          <Button variant="outline" size="sm">
                            <Truck className="h-4 w-4 mr-2" />Buy label
                          </Button>
                        </Link>
                      )}
                      <Link to="/app/shipments/$shipmentId" params={{ shipmentId: s.id }}>
                        <Button variant="ghost" size="sm">Open</Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <OrderRmasPanel orderId={orderId} />

          <OrderWaveMemberships tenantId={order.tenant_id} orderId={orderId} />

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Inventory Custody</CardTitle></CardHeader>
            <CardContent>
              <Tabs defaultValue="order">
                <TabsList>
                  <TabsTrigger value="order">This order</TabsTrigger>
                  <TabsTrigger value="customer">Customer activity</TabsTrigger>
                </TabsList>
                <TabsContent value="order" className="mt-3">
                  <CustodyTimeline tenantId={order.tenant_id} entity="order" id={orderId} limit={200} />
                </TabsContent>
                <TabsContent value="customer" className="mt-3">
                  <CustodyTimeline tenantId={order.tenant_id} entity="tenant" limit={50} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar — actions + timeline */}
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <OrderHoldsPanel orderId={orderId} />
          <OrderStatusActions
            orderId={orderId}
            status={order.status as OrderStatus}
            version={(order as any).version ?? 0}
            holdReason={(order as any).hold_reason ?? null}
            internalNotes={(order as any).internal_notes ?? null}
            notes={(order as any).notes ?? null}
            tags={(order as any).tags ?? []}
            priority={(order as any).priority ?? 5}
          />
          <OrderTimeline orderId={orderId} />
        </aside>
      </div>

      <Dialog open={shipOpen} onOpenChange={setShipOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ship order</DialogTitle></DialogHeader>
          <form onSubmit={ship} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Carrier</Label>
                {carriers.length > 0 ? (
                  <Select value={shipCarrier} onValueChange={(v) => { setShipCarrier(v); setShipService(""); }}>
                    <SelectTrigger><SelectValue placeholder={order.carrier ?? "Select carrier"} /></SelectTrigger>
                    <SelectContent>{carriers.map((c) => <SelectItem key={c.id} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={shipCarrier} onChange={(e) => setShipCarrier(e.target.value)} placeholder={order.carrier ?? "UPS"} />
                )}
              </div>
              <div className="space-y-2">
                <Label>Service</Label>
                {(() => {
                  const carrier = carriers.find((c) => c.code === shipCarrier);
                  const svcs = carrier ? carrierServices.filter((s) => s.carrier_id === carrier.id) : [];
                  return svcs.length > 0 ? (
                    <Select value={shipService} onValueChange={setShipService}>
                      <SelectTrigger><SelectValue placeholder={order.service_level ?? "Select service"} /></SelectTrigger>
                      <SelectContent>{svcs.map((s) => <SelectItem key={s.id} value={s.code}>{s.code} — {s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input value={shipService} onChange={(e) => setShipService(e.target.value)} placeholder={order.service_level ?? "Ground"} />
                  );
                })()}
              </div>
            </div>
            <div className="space-y-2"><Label>Tracking number</Label><Input required value={shipTracking} onChange={(e) => setShipTracking(e.target.value)} /></div>
            {carriers.length === 0 && (
              <p className="text-xs text-muted-foreground">Tip: add carriers in Admin → Carriers to pick from a dropdown.</p>
            )}
            <DialogFooter><Button type="submit" disabled={busy === "ship"}>{busy === "ship" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm shipment</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

type RmaLineRow = { id: string; sku_id: string; qty_shipped: number; qty_ordered: number };

function CreateRmaButton(props: {
  orderId: string;
  orderNumber: string;
  lines: RmaLineRow[];
  skuLabel: (id: string) => string;
  createRmaFn: (args: { data: any }) => Promise<any>;
  onCreated: (rmaId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const eligible = props.lines.filter((l) => (l.qty_shipped ?? 0) > 0);
  const [qtys, setQtys] = useState<Record<string, string>>({});

  const initIfNeeded = (next: boolean) => {
    setOpen(next);
    if (next) {
      const init: Record<string, string> = {};
      for (const l of eligible) init[l.sku_id] = String(l.qty_shipped);
      setQtys(init);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const lines = eligible
      .map((l) => ({ skuId: l.sku_id, qty: parseInt(qtys[l.sku_id] || "0", 10), maxQty: l.qty_shipped }))
      .filter((l) => l.qty > 0);
    if (lines.length === 0) return toast.error("Pick at least one line and quantity");
    if (lines.some((l) => l.qty > l.maxQty)) return toast.error("Quantity cannot exceed shipped");
    setBusy(true);
    try {
      const res = await runServerFn<{ id: string }>(
        () => props.createRmaFn({ data: {
          orderId: props.orderId,
          reason: reason.trim() || null,
          notes: notes.trim() || null,
          lines: lines.map(({ skuId, qty }) => ({ skuId, qty })),
          idempotencyKey: `rma-${props.orderId}-${Date.now()}`,
        } }) as Promise<{ id: string }>,
        "Create RMA",
      );
      toast.success("RMA created");
      setOpen(false);
      props.onCreated(res.id);
    } catch { /* toasted */ } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={initIfNeeded}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={eligible.length === 0}>
          <Undo2 className="h-4 w-4 mr-2" />Create RMA
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader><DialogTitle>Create RMA from {props.orderNumber}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="border rounded-md">
            <Table>
              <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead className="text-right">Shipped</TableHead><TableHead className="text-right w-[120px]">Return qty</TableHead></TableRow></TableHeader>
              <TableBody>
                {eligible.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground text-sm">No shipped lines available to return.</TableCell></TableRow>
                ) : eligible.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-sm">{props.skuLabel(l.sku_id)}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.qty_shipped}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number" min={0} max={l.qty_shipped}
                        value={qtys[l.sku_id] ?? ""}
                        onChange={(e) => setQtys((q) => ({ ...q, [l.sku_id]: e.target.value }))}
                        className="h-8 w-20 ml-auto text-right"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="space-y-2"><Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Customer reported damage on arrival" />
          </div>
          <div className="space-y-2"><Label>Internal notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy || eligible.length === 0}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create RMA
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrderRoutingCard({
  orderId,
  status,
  initialSizeTier,
  initialStrategy,
  onSaved,
}: {
  orderId: string;
  status: OrderStatus;
  initialSizeTier: "standard" | "bulk" | "retail" | null;
  initialStrategy: string | null;
  onSaved?: () => void;
}) {
  const fn = useServerFn(updateOrderRouting);
  const [sizeTier, setSizeTier] = useState<string>(initialSizeTier ?? "standard");
  const [strategy, setStrategy] = useState<string>(initialStrategy ?? "default");
  const [busy, setBusy] = useState(false);
  const disabled = !canEdit(status, "carrier");
  const dirty = sizeTier !== (initialSizeTier ?? "standard") || strategy !== (initialStrategy ?? "default");
  const save = async () => {
    setBusy(true);
    try {
      await runServerFn(
        () => fn({ data: { orderId, size_tier: sizeTier as any, routing_strategy: strategy } }),
        "Update routing",
      );
      toast.success("Routing updated");
      onSaved?.();
    } catch { /* toasted */ }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Routing</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Size tier</Label>
          <Select value={sizeTier} onValueChange={setSizeTier} disabled={disabled}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="bulk">Bulk / pallet</SelectItem>
              <SelectItem value="retail">Retail compliance</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Strategy</Label>
          <Select value={strategy} onValueChange={setStrategy} disabled={disabled}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default wave</SelectItem>
              <SelectItem value="single_line">Single-line / ecom</SelectItem>
              <SelectItem value="multi_line_batch">Multi-line batch</SelectItem>
              <SelectItem value="bulk_pallet">Bulk pallet</SelectItem>
              <SelectItem value="retail_compliant">Retail compliant</SelectItem>
              <SelectItem value="rush">Rush / SLA</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button size="sm" onClick={save} disabled={!dirty || busy || disabled} className="w-full sm:w-auto">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save routing
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function OrderCostCard({ orderId }: { orderId: string }) {
  const fn = useServerFn(getOrderCostBreakdown);
  const { data, isLoading } = useQuery({
    queryKey: ["order-cost", orderId],
    queryFn: () => fn({ data: { orderId } }),
  });
  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const labels: Record<string, string> = {
    labor_pick: "Pick labor",
    labor_pack: "Pack labor",
    labor_receive: "Receive labor",
    packaging: "Packaging",
    freight: "Freight",
    storage_per_day: "Storage",
  };
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Cost to Serve</CardTitle>
        {data?.run_id && <span className="text-xs text-muted-foreground">Latest run</span>}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : !data || data.components.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No cost-to-serve data yet. Run a sweep from Analytics → Cost to Serve.
          </div>
        ) : (
          <div className="space-y-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.components.map((c) => (
                  <TableRow key={c.component}>
                    <TableCell className="text-sm">{labels[c.component] ?? c.component}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{c.qty || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{fmt(c.total_cents)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-semibold">Total cost</TableCell>
                  <TableCell />
                  <TableCell className="text-right tabular-nums font-semibold">{fmt(data.cost_total_cents)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}